import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, IdCard } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { ErrorState } from '@/components/common/States';
import { Spinner } from '@/components/common/Spinner';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { transportDriversApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type {
  DriverDocumentType,
  DriverRole,
  DriverStatus,
  LicenseType,
  TrainingStatus,
  TransportDocument,
} from '@/types/transport';
import { formatDate, orDash } from '@/utils/format';
import {
  Blockers,
  ComplianceBadge,
  DriverStatusBadge,
  Field,
  Stat,
  humanise,
} from './shared';

const DOCUMENT_TYPES: DriverDocumentType[] = [
  'DRIVING_LICENSE',
  'MEDICAL_CERTIFICATE',
  'POLICE_VERIFICATION',
  'TRAINING_CERTIFICATE',
  'IDENTIFICATION',
  'OTHER',
];

const LICENSE_TYPES: LicenseType[] = ['LTV', 'HTV', 'PSV', 'MOTORCYCLE', 'OTHER'];

const TRAINING_STATUSES: TrainingStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'EXPIRED'];

/**
 * The transitions a coordinator may push a driver through. Benching in either
 * direction needs a recorded why — the server refuses without one, so the
 * prompt does not pretend otherwise.
 */
const STATUS_ACTIONS: {
  target: DriverStatus;
  label: string;
  ask: string;
  reasonRequired: boolean;
  danger?: boolean;
}[] = [
  { target: 'ACTIVE', label: 'Reinstate', ask: 'Why are they back on duty? (optional)', reasonRequired: false },
  { target: 'ON_LEAVE', label: 'Put on leave', ask: 'Why are they going on leave? (optional)', reasonRequired: false },
  {
    target: 'INACTIVE',
    label: 'Mark inactive',
    ask: 'Why are they coming off the roster?',
    reasonRequired: true,
  },
  {
    target: 'SUSPENDED',
    label: 'Suspend',
    ask: 'Why are they being suspended? Routes they serve will need cover.',
    reasonRequired: true,
    danger: true,
  },
];

/** Bytes, in the units a person reads. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * One driver: their papers, their standing, and whether a trip may carry them.
 *
 * <p>The eligibility verdict sits above everything because it is the question
 * this page is opened to answer, and the blockers are the server's words
 * verbatim — restating them here is how the two would come to disagree.
 * Transport's status is separate from employment status, which HR owns.
 */
export function DriverDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const manages = can(PERMISSIONS.TRANSPORT_DRIVER_MANAGE);
  const seesDocuments = can(PERMISSIONS.TRANSPORT_DRIVER_DOCUMENT_VIEW);
  const managesDocuments = can(PERMISSIONS.TRANSPORT_DRIVER_DOCUMENT_MANAGE);

  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState<DriverRole>('DRIVER');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseType, setLicenseType] = useState<LicenseType | ''>('');
  const [licenseIssueDate, setLicenseIssueDate] = useState('');
  const [licenseExpiryDate, setLicenseExpiryDate] = useState('');
  const [medicalCheckDate, setMedicalCheckDate] = useState('');
  const [medicalExpiryDate, setMedicalExpiryDate] = useState('');
  const [trainingStatus, setTrainingStatus] = useState<TrainingStatus | ''>('');
  const [notes, setNotes] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DriverDocumentType>('DRIVING_LICENSE');
  const [docTitle, setDocTitle] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [docAuthority, setDocAuthority] = useState('');
  const [docIssuedDate, setDocIssuedDate] = useState('');
  const [docExpiryDate, setDocExpiryDate] = useState('');
  const [deletingDoc, setDeletingDoc] = useState<TransportDocument | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const driver = useQuery({
    queryKey: queryKeys.transportDrivers.detail(id),
    queryFn: () => transportDriversApi.get(id),
  });

  const documents = useQuery({
    queryKey: queryKeys.transportDrivers.documents(id),
    queryFn: () => transportDriversApi.documents(id),
    enabled: seesDocuments,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.transportDrivers.all });

  const setStatus = useMutation({
    mutationFn: ({ target, reason }: { target: DriverStatus; reason?: string }) =>
      transportDriversApi.setStatus(id, target, reason),
    onSuccess: (updated) => {
      toast.success(`${updated.employeeName} is now ${humanise(updated.status)}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const update = useMutation({
    mutationFn: () =>
      transportDriversApi.update(id, {
        employeeId: driver.data!.employeeId,
        driverRole: role,
        licenseNumber: licenseNumber.trim() === '' ? undefined : licenseNumber.trim(),
        licenseType: licenseType === '' ? undefined : licenseType,
        licenseIssueDate: licenseIssueDate === '' ? undefined : licenseIssueDate,
        licenseExpiryDate: licenseExpiryDate === '' ? undefined : licenseExpiryDate,
        medicalCheckDate: medicalCheckDate === '' ? undefined : medicalCheckDate,
        medicalExpiryDate: medicalExpiryDate === '' ? undefined : medicalExpiryDate,
        trainingStatus: trainingStatus === '' ? undefined : trainingStatus,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      }),
    onSuccess: (updated) => {
      toast.success(`${updated.employeeName} updated`);
      setEditing(false);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const upload = useMutation({
    mutationFn: () => {
      if (file === null) {
        throw new Error('Choose a file first');
      }
      return transportDriversApi.uploadDocument(id, file, {
        documentType,
        title: docTitle.trim() === '' ? undefined : docTitle.trim(),
        documentNumber: docNumber.trim() === '' ? undefined : docNumber.trim(),
        issuingAuthority: docAuthority.trim() === '' ? undefined : docAuthority.trim(),
        issuedDate: docIssuedDate === '' ? undefined : docIssuedDate,
        expiryDate: docExpiryDate === '' ? undefined : docExpiryDate,
      });
    },
    onSuccess: () => {
      toast.success('Document uploaded');
      setUploadOpen(false);
      setFile(null);
      setDocTitle('');
      setDocNumber('');
      setDocAuthority('');
      setDocIssuedDate('');
      setDocExpiryDate('');
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const removeDocument = useMutation({
    mutationFn: (documentId: string) => transportDriversApi.deleteDocument(id, documentId),
    onSuccess: () => {
      toast.success('Document removed');
      setDeletingDoc(null);
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setDeletingDoc(null);
    },
  });

  const openEdit = () => {
    const data = driver.data;
    if (data === undefined) {
      return;
    }
    setRole(data.driverRole);
    setLicenseNumber(data.licenseNumber ?? '');
    setLicenseType(data.licenseType ?? '');
    setLicenseIssueDate(data.licenseIssueDate ?? '');
    setLicenseExpiryDate(data.licenseExpiryDate ?? '');
    setMedicalCheckDate(data.medicalCheckDate ?? '');
    setMedicalExpiryDate(data.medicalExpiryDate ?? '');
    setTrainingStatus(data.trainingStatus ?? '');
    setNotes(data.notes ?? '');
    setEditing(true);
  };

  const changeStatus = (target: DriverStatus, ask: string, reasonRequired: boolean) => {
    const why = window.prompt(ask);
    if (why === null) {
      return;
    }
    const reason = why.trim();
    if (reasonRequired && reason === '') {
      return;
    }
    setStatus.mutate({ target, reason: reason === '' ? undefined : reason });
  };

  if (driver.isError) {
    return <ErrorState error={driver.error} onRetry={() => void driver.refetch()} />;
  }
  if (driver.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  const data = driver.data;

  return (
    <div>
      <Link
        to={ROUTES.transportDrivers}
        className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted hover:text-accent"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        All drivers
      </Link>

      <PageHeader
        title={data.employeeName}
        description={`${orDash(data.employeeNumber)} · ${humanise(data.driverRole)}`}
        icon={<IdCard className="h-5 w-5" aria-hidden="true" />}
        action={<DriverStatusBadge status={data.status} />}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Licence"
          value={<ComplianceBadge state={data.licenseState} />}
          hint={
            data.licenseExpiryDate === undefined
              ? 'No expiry on file'
              : `Expires ${formatDate(data.licenseExpiryDate)}`
          }
        />
        <Stat
          label="Medical"
          value={<ComplianceBadge state={data.medicalState} />}
          hint={
            data.medicalExpiryDate === undefined
              ? 'No expiry on file'
              : `Expires ${formatDate(data.medicalExpiryDate)}`
          }
        />
        <Stat
          label="Training"
          value={data.trainingStatus === undefined ? '—' : humanise(data.trainingStatus)}
        />
        <Stat
          label="Trips"
          value={data.tripEligible ? 'Eligible' : 'Blocked'}
          tone={data.tripEligible ? 'success' : 'danger'}
          hint={
            data.tripEligible ? 'May be put on a trip today' : 'The server’s reasons are below'
          }
        />
      </div>

      {!data.tripEligible && (
        <div className="mt-3">
          <Blockers reasons={data.eligibilityBlockers} title="Cannot be put on a trip" />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {seesDocuments && (
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Papers
              </h2>
              {managesDocuments && (
                <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
                  Upload
                </Button>
              )}
            </div>
            {documents.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : documents.isError ? (
              <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
            ) : (documents.data ?? []).length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                Nothing on file. The licence and medical dates above stand on their own, but the
                paper behind them belongs here.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-line">
                {(documents.data ?? []).map((document) => (
                  <li
                    key={document.id}
                    className="flex flex-wrap items-start justify-between gap-3 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink">
                        {document.title ?? humanise(document.documentType)}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-subtle">
                        {document.fileName} · {formatSize(document.fileSize)}
                        {document.documentNumber !== undefined && ` · nº ${document.documentNumber}`}
                      </p>
                      {(document.issuedDate !== undefined || document.expiryDate !== undefined) && (
                        <p className="mt-0.5 text-xs text-subtle">
                          {document.issuedDate !== undefined &&
                            `issued ${formatDate(document.issuedDate)}`}
                          {document.issuedDate !== undefined &&
                            document.expiryDate !== undefined &&
                            ' · '}
                          {document.expiryDate !== undefined &&
                            `expires ${formatDate(document.expiryDate)}`}
                        </p>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <ComplianceBadge state={document.state} />
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={downloading === document.id}
                        onClick={() => {
                          setDownloading(document.id);
                          void transportDriversApi
                            .downloadDocument(id, document.id, document.fileName)
                            .catch((error: unknown) => handleApiError(error))
                            .finally(() => setDownloading(null));
                        }}
                      >
                        Download
                      </Button>
                      {managesDocuments && (
                        <Button size="sm" variant="ghost" onClick={() => setDeletingDoc(document)}>
                          Delete
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        <div className="space-y-4">
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Profile
              </h2>
              {manages && (
                <Button size="sm" variant="secondary" onClick={openEdit}>
                  Edit
                </Button>
              )}
            </div>
            <dl className="mt-2">
              <Field label="Role">{humanise(data.driverRole)}</Field>
              <Field label="Licence number">{orDash(data.licenseNumber)}</Field>
              <Field label="Licence type">{orDash(data.licenseType)}</Field>
              <Field label="Licence issued">
                {data.licenseIssueDate === undefined ? '—' : formatDate(data.licenseIssueDate)}
              </Field>
              <Field label="Licence expires">
                {data.licenseExpiryDate === undefined ? '—' : formatDate(data.licenseExpiryDate)}
              </Field>
              <Field label="Medical check">
                {data.medicalCheckDate === undefined ? '—' : formatDate(data.medicalCheckDate)}
              </Field>
              <Field label="Medical expires">
                {data.medicalExpiryDate === undefined ? '—' : formatDate(data.medicalExpiryDate)}
              </Field>
              <Field label="Registered">{formatDate(data.createdAt)}</Field>
              {data.statusReason !== undefined && (
                <Field label="Status reason">{data.statusReason}</Field>
              )}
              {data.notes !== undefined && <Field label="Notes">{data.notes}</Field>}
            </dl>
          </Card>

          {manages && (
            <Card className="p-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.05em] text-ink">
                Standing
              </h2>
              <p className="mt-1 text-xs text-muted">
                Transport's trust in them — separate from employment status, which HR owns.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {STATUS_ACTIONS.filter((action) => action.target !== data.status).map((action) => (
                  <Button
                    key={action.target}
                    size="sm"
                    variant={action.danger === true ? 'danger' : 'secondary'}
                    disabled={setStatus.isPending}
                    onClick={() => changeStatus(action.target, action.ask, action.reasonRequired)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {editing && (
        <Modal
          open
          onClose={() => setEditing(false)}
          title={`Edit ${data.employeeName}`}
          description="The profile belongs to one employee for life — only the papers change."
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              update.mutate();
            }}
          >
            <SelectField
              label="Role"
              required
              value={role}
              onChange={(event) => setRole(event.target.value as DriverRole)}
            >
              <option value="DRIVER">Driver</option>
              <option value="ATTENDANT">Attendant</option>
            </SelectField>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Licence number"
                value={licenseNumber}
                onChange={(event) => setLicenseNumber(event.target.value)}
              />
              <SelectField
                label="Licence type"
                value={licenseType}
                onChange={(event) => setLicenseType(event.target.value as LicenseType | '')}
              >
                <option value="">Not recorded</option>
                {LICENSE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </SelectField>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Licence issued"
                type="date"
                value={licenseIssueDate}
                onChange={(event) => setLicenseIssueDate(event.target.value)}
              />
              <TextField
                label="Licence expires"
                type="date"
                value={licenseExpiryDate}
                onChange={(event) => setLicenseExpiryDate(event.target.value)}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Medical check"
                type="date"
                value={medicalCheckDate}
                onChange={(event) => setMedicalCheckDate(event.target.value)}
              />
              <TextField
                label="Medical expires"
                type="date"
                value={medicalExpiryDate}
                onChange={(event) => setMedicalExpiryDate(event.target.value)}
              />
            </div>

            <SelectField
              label="Training"
              value={trainingStatus}
              onChange={(event) => setTrainingStatus(event.target.value as TrainingStatus | '')}
            >
              <option value="">Leave as it is</option>
              {TRAINING_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>

            <TextField
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      <Modal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload a document"
        description="The compliance state is derived from the expiry date — a paper with no expiry shows as undated, on purpose."
        footer={
          <>
            <Button variant="ghost" onClick={() => setUploadOpen(false)} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button onClick={() => upload.mutate()} loading={upload.isPending} disabled={file === null}>
              Upload
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="driver-document-file" className="mb-1.5 block text-xs font-medium text-muted">
              File<span className="ml-0.5 text-danger">*</span>
            </label>
            <input
              id="driver-document-file"
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm file:text-ink"
            />
          </div>

          <SelectField
            label="Document type"
            required
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value as DriverDocumentType)}
          >
            {DOCUMENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {humanise(type)}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Title"
            value={docTitle}
            onChange={(event) => setDocTitle(event.target.value)}
            placeholder="Optional label"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Document number"
              value={docNumber}
              onChange={(event) => setDocNumber(event.target.value)}
            />
            <TextField
              label="Issuing authority"
              value={docAuthority}
              onChange={(event) => setDocAuthority(event.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Issued"
              type="date"
              value={docIssuedDate}
              onChange={(event) => setDocIssuedDate(event.target.value)}
            />
            <TextField
              label="Expires"
              type="date"
              value={docExpiryDate}
              onChange={(event) => setDocExpiryDate(event.target.value)}
              hint="When set, the paper starts warning before this date passes"
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deletingDoc !== null}
        destructive
        title="Delete this document?"
        message={`${deletingDoc?.fileName ?? ''} will be removed from storage. This cannot be undone.`}
        confirmLabel="Delete"
        loading={removeDocument.isPending}
        onCancel={() => setDeletingDoc(null)}
        onConfirm={() => {
          if (deletingDoc !== null) {
            removeDocument.mutate(deletingDoc.id);
          }
        }}
      />
    </div>
  );
}
